import { Monitor, Computer, FlaskConical, Library, TreePine, Bus, ShieldCheck, Droplets } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { FACILITIES } from '@/data/content';

const FAC_ICONS = [Monitor, Computer, FlaskConical, Library, TreePine, Bus, ShieldCheck, Droplets];
const SPAN = { wide: 'md:col-span-2', tall: 'md:row-span-2', normal: '' };

const Infrastructure = () => (
  <>
    <PageHero eyebrow="Infrastructure" title="A campus built for learning." description="Smart classrooms, modern labs, a safe playground and CCTV-monitored premises — everything a growing mind needs." />

    <section className="py-24 sm:py-32" data-testid="facilities-bento">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="01" eyebrow="Our Facilities" title="Every corner, thoughtfully built" />
        <div className="mt-14 grid md:grid-cols-4 auto-rows-[190px] gap-4">
          {FACILITIES.map((f, i) => {
            const Icon = FAC_ICONS[i];
            return (
              <Reveal key={f.title} delay={i * 0.06} className={SPAN[f.span] || ''}>
                <div data-testid={`facility-${i}`} className="group relative h-full w-full overflow-hidden rounded-lg bg-navy p-6 flex flex-col justify-end transition-all hover:-translate-y-1">
                  <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-navy-light transition-transform duration-500 group-hover:scale-150" aria-hidden="true" />
                  <Icon className="absolute right-5 top-5 h-9 w-9 text-gold" />
                  <h3 className="relative font-display text-xl font-bold text-cream">{f.title}</h3>
                  <p className="relative mt-2 text-sm text-cream/60 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section className="bg-cream-dark py-24 sm:py-32" data-testid="safety-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 items-center">
        <div>
          <SectionHeading number="02" eyebrow="Safety First" title="A campus parents can trust" />
          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-4">
              {[
                '24×7 CCTV coverage across classrooms, corridors and entry gates.',
                'Guarded single-entry campus with visitor register.',
                'GPS-tracked buses with trained drivers and a lady attendant on primary routes.',
                'Regular fire-safety drills and first-aid trained staff.',
              ].map((t, i) => (
                <li key={i} data-testid={`safety-${i}`} className="flex gap-3 rounded-lg bg-white border border-navy/10 p-4 text-sm text-navy/80 leading-relaxed">
                  <ShieldCheck className="h-5 w-5 text-crimson shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="relative">
            <div className="absolute -right-4 -top-4 h-full w-full rounded-lg border-2 border-gold" aria-hidden="true" />
            <img src="/images/hero-building.jpg" alt="Ideal Public School campus building" loading="lazy" className="relative rounded-lg w-full object-cover aspect-[4/3]" />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Infrastructure;
